/**
 * Dudhiya Milk Calculation Utility Functions
 * Centralized calculation algorithms for milk collection, fat/snf pricing, and pro-rata rates.
 */

/** Supported CLR Conversion Factors in Dudhiya Web App */
export const VALID_CLR_CONVERSION_FACTORS = [0.14, 0.50];

/** Supported FAT/SNF Ratios in Dudhiya Web App */
export const VALID_FAT_SNF_RATIOS = ['60/40', '52/48'];

/**
 * Calculates SNF percentage from CLR (Lactometer Reading) and FAT percentage.
 * Formula: SNF = (CLR / 4) + (0.20 * FAT) + conversionFactor
 * Supported conversion factors: 0.14 or 0.50
 * 
 * @param {number|string} clrValue - CLR lactometer reading
 * @param {number|string} fatValue - FAT percentage
 * @param {number|string} clrConversionFactor - Conversion factor (supported values: 0.14 or 0.50, default: 0.14)
 * @returns {number|null} Calculated SNF percentage rounded to 2 decimal places, or null if invalid inputs
 */
export const calculateSnfFromClr = (clrValue, fatValue, clrConversionFactor = 0.14) => {
  if (!clrValue || !fatValue) return null;
  const clr = parseFloat(clrValue);
  const fat = parseFloat(fatValue);
  const conversionFactor = parseFloat(clrConversionFactor) || 0.14;

  if (isNaN(clr) || isNaN(fat) || clr <= 0 || fat <= 0) return null;
  return Math.floor(((clr / 4) + (fat * 0.20) + conversionFactor) * 100) / 100;
};

/**
 * Converts Milk Weight in Kg to Liters.
 * Density Constant: 1.02249 kg/L
 * Formula: Liters = Weight / 1.02249
 * 
 * @param {number|string} weightKg - Milk weight in Kg
 * @returns {number} Milk volume in Liters rounded down to 2 decimal places
 */
export const calculateLitersFromKg = (weightKg) => {
  const weight = parseFloat(weightKg) || 0;
  return Math.floor((weight / 1.02249) * 100) / 100;
};

/**
 * Calculates total Fat weight in Kg.
 * Formula: Fat Kg = Weight * (FAT % / 100)
 * 
 * @param {number|string} weightKg - Milk weight in Kg
 * @param {number|string} fatPercentage - FAT percentage
 * @returns {number} Fat weight in Kg rounded down to 2 decimal places
 */
export const calculateFatKg = (weightKg, fatPercentage) => {
  const weight = parseFloat(weightKg) || 0;
  const fat = parseFloat(fatPercentage) || 0;
  return Math.floor((weight * (fat / 100)) * 100) / 100;
};

/**
 * Calculates total SNF weight in Kg.
 * Formula: SNF Kg = Weight * (SNF % / 100)
 * 
 * @param {number|string} weightKg - Milk weight in Kg
 * @param {number|string} snfPercentage - SNF percentage
 * @returns {number} SNF weight in Kg rounded down to 2 decimal places
 */
export const calculateSnfKg = (weightKg, snfPercentage) => {
  const weight = parseFloat(weightKg) || 0;
  const snf = parseFloat(snfPercentage) || 0;
  return Math.floor((weight * (snf / 100)) * 100) / 100;
};

/**
 * Calculates Fat Component Purchase Rate per Kg based on Fat/SNF Ratio.
 * Supported Ratios:
 * - '60/40' (60_40): Fat Ratio % = 60%
 * - '52/48' (52_48): Fat Ratio % = 52%
 * Formula: Fat Rate = (Milk Rate * Fat Ratio %) / 6.5
 * 
 * @param {number|string} milkRate - Base rate per kg of milk
 * @param {string} fatSnfRatio - Ratio choice ('60/40', '60_40', '52/48', '52_48')
 * @returns {number} Fat component rate per kg rounded down to 2 decimal places
 */
export const calculateFatRate = (milkRate, fatSnfRatio = '60/40') => {
  const rate = parseFloat(milkRate) || 0;
  const normalizedRatio = String(fatSnfRatio).replace('_', '/');
  const fatRatioPercent = (normalizedRatio === '60/40') ? 60 : 52;
  return Math.floor((rate * fatRatioPercent / 6.5) * 100) / 100;
};

/**
 * Calculates SNF Component Purchase Rate per Kg based on Fat/SNF Ratio.
 * Supported Ratios:
 * - '60/40' (60_40): SNF Ratio % = 40%
 * - '52/48' (52_48): SNF Ratio % = 48%
 * Formula: SNF Rate = (Milk Rate * SNF Ratio %) / Base SNF %
 * 
 * @param {number|string} milkRate - Base rate per kg of milk
 * @param {string} fatSnfRatio - Ratio choice ('60/40', '60_40', '52/48', '52_48')
 * @param {number|string} baseSnfPercentage - Base SNF percentage reference (default: 9.0)
 * @returns {number} SNF component rate per kg rounded down to 2 decimal places
 */
export const calculateSnfRate = (milkRate, fatSnfRatio = '60/40', baseSnfPercentage = 9.0) => {
  const rate = parseFloat(milkRate) || 0;
  const baseSnf = parseFloat(baseSnfPercentage) || 9.0;
  const normalizedRatio = String(fatSnfRatio).replace('_', '/');
  const snfRatioPercent = (normalizedRatio === '60/40') ? 40 : 48;
  return Math.floor((rate * snfRatioPercent / baseSnf) * 100) / 100;
};

/**
 * Calculates Total Amount in Standard (Non-Pro-Rata) Mobile APK Style.
 * Each component (Fat Amount & SNF Amount) is floored independently to 2 decimals.
 * 
 * @param {number|string} fatKg - Fat solid weight in kg
 * @param {number|string} snfKg - SNF solid weight in kg
 * @param {number|string} fatRate - Fat component rate per kg
 * @param {number|string} snfRate - SNF component rate per kg
 * @returns {number} Total payable amount rounded to 2 decimal places
 */
export const calculateAmountMobileAPKStyle = (fatKg, snfKg, fatRate, snfRate) => {
  const fatAmount = Math.floor(parseFloat(fatKg || 0) * parseFloat(fatRate || 0) * 100) / 100;
  const snfAmount = Math.floor(parseFloat(snfKg || 0) * parseFloat(snfRate || 0) * 100) / 100;
  return parseFloat((fatAmount + snfAmount).toFixed(2));
};

/**
 * Calculates Equivalent Solid Weight from Amount and Milk Rate.
 * Formula: Solid Weight = Amount / Milk Rate
 * 
 * @param {number|string} amount - Total amount
 * @param {number|string} milkRate - Base milk rate
 * @returns {string} Solid weight formatted to 3 decimal places
 */
export const calculateSolidWeight = (amount, milkRate) => {
  const amt = parseFloat(amount) || 0;
  const rate = parseFloat(milkRate) || 1;
  return (amt / rate).toFixed(3);
};

/**
 * Resolves Fat Step-Up rate from threshold array for Pro-Rata calculations.
 * Sorts thresholds ascending by step and finds the highest applicable threshold.
 * 
 * @param {number} fatPercent - Measured FAT percentage
 * @param {Array<{step: string|number, rate: string|number}>} thresholds - Step-up threshold rate chart
 * @param {number|string} defaultRate - Default step-up rate if thresholds empty
 * @returns {number} Applicable fat step-up rate
 */
export const resolveRateFromFatThresholds = (fatPercent, thresholds = [], defaultRate = 0.80) => {
  if (!Array.isArray(thresholds) || thresholds.length === 0) {
    return parseFloat(defaultRate) || 0;
  }

  const sortedThresholds = [...thresholds]
    .filter(t => !isNaN(parseFloat(t.step)) && !isNaN(parseFloat(t.rate)))
    .sort((a, b) => parseFloat(a.step) - parseFloat(b.step));

  let applicableRate = 0;
  for (let i = 0; i < sortedThresholds.length; i++) {
    const threshold = parseFloat(sortedThresholds[i].step);
    const tRate = parseFloat(sortedThresholds[i].rate);
    if (fatPercent >= threshold) {
      applicableRate = tRate;
    } else {
      break;
    }
  }

  return applicableRate;
};

/**
 * Resolves SNF Step-Down rate from threshold array for Pro-Rata calculations.
 * Sorts thresholds descending by step and finds the highest threshold where SNF < threshold.step.
 * 
 * @param {number} snfPercent - Measured SNF percentage
 * @param {Array<{step: string|number, rate: string|number}>} thresholds - Step-down threshold rate chart
 * @param {number|string} defaultRate - Default step-down rate if thresholds empty
 * @returns {number} Applicable SNF step-down rate
 */
export const resolveRateFromSnfThresholds = (snfPercent, thresholds = [], defaultRate = 0.27) => {
  if (!Array.isArray(thresholds) || thresholds.length === 0) {
    return parseFloat(defaultRate) || 0;
  }

  const sortedThresholds = [...thresholds]
    .filter(t => !isNaN(parseFloat(t.step)) && !isNaN(parseFloat(t.rate)))
    .sort((a, b) => parseFloat(b.step) - parseFloat(a.step));

  let applicableRate = 0;
  for (let i = 0; i < sortedThresholds.length; i++) {
    const threshold = parseFloat(sortedThresholds[i].step);
    const tRate = parseFloat(sortedThresholds[i].rate);
    if (snfPercent < threshold) {
      applicableRate = tRate;
      break;
    }
  }

  return Math.abs(applicableRate);
};

/**
 * Computes complete derived values for Standard Collections.
 * 
 * @param {Object} collection - Collection object containing weight, fat, snf, milk_rate, etc.
 * @param {string} defaultFatSnfRatio - Fallback FAT/SNF ratio (default: '60/40')
 * @param {number} defaultBaseSnf - Fallback Base SNF (default: 9.0)
 * @returns {Object} Derived values object containing fat_kg, snf_kg, fat_rate, snf_rate, solid_weight, amount, etc.
 */
export const calculateDerivedValuesStandard = (collection, defaultFatSnfRatio = '60/40', defaultBaseSnf = 9.0) => {
  const fat = parseFloat(collection.fat_percent || collection.fat_percentage || 0);
  const snf = parseFloat(collection.snf_percent || collection.snf_percentage || 0);
  const weight = parseFloat(collection.weight || collection.kg || 0);
  const rate = parseFloat(collection.milk_rate || 0);

  const collectionBaseSnf = parseFloat(collection.base_snf || collection.base_snf_percentage || defaultBaseSnf);
  const collectionFatSnfRatio = (collection.fat_snf_ratio || defaultFatSnfRatio).replace('_', '/');
  const collectionClrConversionFactor = collection.clr_conversion || collection.clr_conversion_factor || 0.14;

  const fatKg = calculateFatKg(weight, fat);
  const snfKg = calculateSnfKg(weight, snf);

  const fatRate = calculateFatRate(rate, collectionFatSnfRatio);
  const snfRate = calculateSnfRate(rate, collectionFatSnfRatio, collectionBaseSnf);

  const amount = calculateAmountMobileAPKStyle(fatKg, snfKg, fatRate, snfRate);
  const solidWeight = calculateSolidWeight(amount, rate);

  return {
    fat_kg: fatKg,
    snf_kg: snfKg,
    fat_rate: fatRate,
    snf_rate: snfRate,
    solid_weight: solidWeight,
    amount: amount,
    finalRate: rate,
    fat_snf_ratio: collectionFatSnfRatio,
    clr_conversion_factor: collectionClrConversionFactor
  };
};

/**
 * Computes complete derived values for Pro-Rata Collections.
 * 
 * @param {Object} collection - Collection object
 * @param {string} defaultFatSnfRatio - Fallback FAT/SNF ratio
 * @param {number} defaultBaseSnf - Fallback Base SNF
 * @param {Array} globalFatStepUpRates - Fallback global Fat step-up rates
 * @param {Array} globalSnfStepDownRates - Fallback global SNF step-down rates
 * @returns {Object} Derived values object including is_pro_rata flag and finalRate
 */
export const calculateDerivedValuesProRata = (
  collection,
  defaultFatSnfRatio = '60_40',
  defaultBaseSnf = 9.0,
  globalFatStepUpRates = [{ step: '6.50', rate: '0.80' }],
  globalSnfStepDownRates = [{ step: '9.00', rate: '0.27' }]
) => {
  const fat = parseFloat(collection.fat_percent || collection.fat_percentage || 0);
  const snf = parseFloat(collection.snf_percent || collection.snf_percentage || 0);
  const weight = parseFloat(collection.weight || collection.kg || 0);
  const rate = parseFloat(collection.milk_rate || 0);
  const baseSnf = parseFloat(collection.base_snf || collection.base_snf_percentage || defaultBaseSnf);

  const collectionFatSnfRatio = collection.fat_snf_ratio || defaultFatSnfRatio;
  const fatKg = calculateFatKg(weight, fat);
  const snfKg = calculateSnfKg(weight, snf);

  const fatRate = calculateFatRate(rate, collectionFatSnfRatio);
  const snfRate = calculateSnfRate(rate, collectionFatSnfRatio, baseSnf);

  let finalRate = rate;
  let amount = 0;

  const collectionRateChart = collection.pro_rata_collection_rate_chart;
  const effectiveFatStepUpRates = collectionRateChart?.fat_step_up_rates || globalFatStepUpRates;
  const effectiveSnfStepDownRates = collectionRateChart?.snf_step_down_rates || globalSnfStepDownRates;

  const isProRata = Array.isArray(effectiveFatStepUpRates) && effectiveFatStepUpRates.some((t) => {
    const threshold = parseFloat(t?.step || t?.threshold);
    const tRate = parseFloat(t?.rate);
    return !isNaN(threshold) && !isNaN(tRate) && !isNaN(fat) && fat >= threshold;
  });

  if (isProRata) {
    const appliedFatRate = resolveRateFromFatThresholds(fat, effectiveFatStepUpRates);
    const appliedSnfRate = resolveRateFromSnfThresholds(snf, effectiveSnfStepDownRates);

    const fatStepUpRateValue = (parseFloat(appliedFatRate) * 10) || 0;
    const snfStepDownRateValue = (Math.abs(parseFloat(appliedSnfRate)) * 10) || 0;

    const fatAdjustment = (fat - 6.5) * fatStepUpRateValue;
    const snfAdjustment = (snf - baseSnf) * snfStepDownRateValue;

    finalRate = rate + fatAdjustment + snfAdjustment;
    amount = parseFloat((finalRate * weight).toFixed(2));
  } else {
    const fatAmount = parseFloat(fatKg) * parseFloat(fatRate);
    const snfAmount = parseFloat(snfKg) * parseFloat(snfRate);
    amount = Math.round((fatAmount + snfAmount) * 100) / 100;
  }

  // Solid weight uses the STANDARD amount (fat/snf component totals), not the
  // pro-rata adjusted amount, so rate adjustments do not inflate solid weight.
  const standardAmount = calculateAmountMobileAPKStyle(fatKg, snfKg, fatRate, snfRate);
  const solidWeight = calculateSolidWeight(standardAmount, rate);

  return {
    fat_kg: fatKg,
    snf_kg: snfKg,
    fat_rate: fatRate,
    snf_rate: snfRate,
    solid_weight: solidWeight,
    amount: amount,
    finalRate: finalRate,
    is_pro_rata: isProRata
  };
};

/**
 * Applies the 0.1% bank charge to a total, honouring the dairy's toggle.
 * When bank charges are disabled the total passes through undeducted and is
 * rounded half-up to a whole rupee, matching the backend's ROUND_HALF_UP so the
 * screen and the PDF agree. The enabled branch keeps the pre-existing floor.
 *
 * @param {number} total - Gross total amount
 * @param {boolean} useBankCharges - Dairy's use_bank_charges flag (default true)
 * @returns {number} Amount after the bank charge, as a whole rupee
 */
export const applyBankCharge = (total, useBankCharges = true) => {
  const amount = parseFloat(total) || 0;
  return useBankCharges ? Math.floor(amount * 0.999) : Math.round(amount);
};

/**
 * Calculates Summary Totals and Averages for a list of collection items.
 *
 * @param {Array<Object>} collectionsList - List of collection items
 * @param {boolean} useBankCharges - Dairy's use_bank_charges flag (default true); controls whether finalDiscountedAmount deducts the 0.1% bank charge
 * @returns {Object} Totals and averages object (totalQuantity, totalFatKg, totalSnfKg, avgFatPercent, avgSnfPercent, totalAmount, finalDiscountedAmount)
 */
export const calculateCollectionTotals = (collectionsList = [], useBankCharges = true) => {
  const totals = collectionsList.reduce((acc, collection) => {
    return {
      count: acc.count + 1,
      totalQuantity: acc.totalQuantity + (parseFloat(collection.kg || collection.weight || 0) || 0),
      totalFatKg: acc.totalFatKg + (parseFloat(collection.fat_kg || 0) || 0),
      totalSnfKg: acc.totalSnfKg + (parseFloat(collection.snf_kg || 0) || 0),
      totalSolidWeight: acc.totalSolidWeight + (parseFloat(collection.solid_weight || 0) || 0),
      totalAmount: acc.totalAmount + (parseFloat(collection.amount || 0) || 0)
    };
  }, { count: 0, totalQuantity: 0, totalFatKg: 0, totalSnfKg: 0, totalSolidWeight: 0, totalAmount: 0 });

  const activeFatRows = collectionsList.filter(c => {
    const val = parseFloat(c.fat_percentage || c.fat_percent || c.fat);
    return !isNaN(val) && val > 0;
  });

  const activeSnfRows = collectionsList.filter(c => {
    const val = parseFloat(c.snf_percentage || c.snf_percent || c.snf);
    return !isNaN(val) && val > 0;
  });

  const avgFatPercent = activeFatRows.length > 0
    ? (activeFatRows.reduce((sum, c) => sum + parseFloat(c.fat_percentage || c.fat_percent || c.fat || 0), 0) / activeFatRows.length)
    : 0;

  const avgSnfPercent = activeSnfRows.length > 0
    ? (activeSnfRows.reduce((sum, c) => sum + parseFloat(c.snf_percentage || c.snf_percent || c.snf || 0), 0) / activeSnfRows.length)
    : 0;

  const finalDiscountedAmount = applyBankCharge(totals.totalAmount, useBankCharges);

  return {
    ...totals,
    avgFatPercent,
    avgSnfPercent,
    finalDiscountedAmount
  };
};
